import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  Container, 
  HeroSection, 
  Header, 
  Logo, 
  Nav, 
  Hamburger,
  MobileMenuOverlay,
  MobileMenu,
  GrabberHandle,
  MobileMenuLogo,
  Divider,
  MenuLink,
  HeroTitle, 
  ContentContainer, 
  Section, 
  SectionTitle, 
  Paragraph, 
  Form, 
  InputWrapper,
  InputField, 
  InputLabel,
  TextAreaWrapper,
  TextArea, 
  TextAreaLabel,
  CheckboxLabel, 
  SubmitButton, 
  AlertMessage, 
  Footer 
} from "../styles/ContactPageStyles";
import logo from "../assets/logo-white.png";
import bgImage from '../assets/bgimagefinal.png';

function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    agree: false,
  });
  const [alert, setAlert] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  // Debugging log to confirm component rendering
  useEffect(() => {
    console.log("ContactPage component rendered");
  }, []);

  const menuItems = [
    { label: "HOME", to: "/" },
    { label: "ABOUT", to: "/about" },
    { label: "CONTACT", to: "/contact" },
    { label: "LOGIN", to: "/login" },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agree) {
      setAlert({ type: "error", message: "You must agree to the terms and conditions." });
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}send-email/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: "procurement@nutrifytoday.com",
          subject: formData.subject,
          body: `
            <div style="font-family: Arial, sans-serif; color: #333;">
              <div style="text-align: center; margin-bottom: 20px;">
                <img src="https://dealsphere.nutrifytoday.com/static/media/th.57a90ebef3dde9740b2d.jpg" alt="Company Logo" style="height: 100px; border-radius: 10px;" />
              </div>
              <h2 style="text-align: center; color: #007bff;">New Contact Message</h2>
              <p><strong>Name:</strong> ${formData.name}</p>
              <p><strong>Email:</strong> ${formData.email}</p>
              <p><strong>Phone:</strong> ${formData.phone}</p>
              <p><strong>Subject:</strong> ${formData.subject}</p>
              <p><strong>Message:</strong></p>
              <p>${formData.message}</p>
              <hr />
              <div style="text-align: center; margin-top: 20px;">
                <p><strong>Contact Information:</strong></p>
                <p><strong>Head Office:</strong> PG51, MGM, 81 Chikasandra, Bangalore, India 560061</p>
                <p><strong>Email:</strong> hello@nutrifytoday.com</p>
                <p><strong>Phone Number:</strong> +91 953 801 1662</p>
              </div>
            </div>
          `,
        }),
      });

      if (response.ok) {
        setAlert({ type: "success", message: "Your message has been sent successfully." });
        setFormData({ name: "", email: "", phone: "", subject: "", message: "", agree: false });
      } else {
        const error = await response.json();
        setAlert({ type: "error", message: error.detail || "Failed to send message." });
      }
    } catch (error) {
      setAlert({ type: "error", message: "An unexpected error occurred." });
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Close menu when clicking outside or on grabber handle
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  // Handle ripple effect and vibration on link tap
  const handleLinkClick = (e, path) => {
    const link = e.currentTarget;
    const ripple = document.createElement("span");
    ripple.classList.add("ripple");
    const rect = link.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
    link.appendChild(ripple);

    // Vibration feedback (if supported)
    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }

    setTimeout(() => {
      closeMenu();
      navigate(path);
    }, 200);
  };

  // Determine active link based on current path
  const getLinkClass = (path) => {
    return location.pathname === path ? "active" : "";
  };

  return (
    <Container>
      <HeroSection bgImage={bgImage}>
        <Header>
          <Logo src={logo} alt="Nutrify Today" onError={() => console.error("Logo failed to load")} />
          <Nav>
            {menuItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className={`menu-item ${getLinkClass(item.to)}`} // Dynamic active link
              >
                {item.label}
              </Link>
            ))}
            <Link to="/signup" className={`menu-item register ${getLinkClass("/signup")}`}>
              REGISTER
            </Link>
          </Nav>
          <Hamburger 
            className={menuOpen ? 'open' : ''} 
            onClick={toggleMenu}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && toggleMenu()}
          >
            <span></span>
            <span></span>
            <span></span>
          </Hamburger>
        </Header>
        <HeroTitle>Contact Us</HeroTitle>
        <MobileMenuOverlay isOpen={menuOpen} onClick={closeMenu} />
        <MobileMenu isOpen={menuOpen} ref={menuRef}>
          <MobileMenuLogo src={logo} alt="Nutrify Today Logo" onError={() => console.error("Mobile menu logo failed to load")} />
          <Divider />
          {menuItems.map((item) => (
            <MenuLink 
              as={Link}
              key={item.label}
              to={item.to} 
              className={getLinkClass(item.to)} 
              onClick={(e) => handleLinkClick(e, item.to)} 
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, item.to)}
            >
              {item.label}
            </MenuLink>
          ))}
          <MenuLink 
            as={Link}
            to="/signup" 
            className={`register ${getLinkClass("/signup")}`} 
            onClick={(e) => handleLinkClick(e, "/signup")} 
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/signup")}
          >
            REGISTER
          </MenuLink>
          <GrabberHandle 
            onClick={closeMenu}
            aria-label="Close menu"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && closeMenu()}
          />
        </MobileMenu>
      </HeroSection>

      <ContentContainer>
        <div>
          <Section>
            <SectionTitle>Head Office</SectionTitle>
            <Paragraph>PG51, MGM, 81 Chikasandra, Bangalore, India 560061</Paragraph>
            <SectionTitle>Quick Contact</SectionTitle>
            <Paragraph>Email: hello@nutrifytoday.com</Paragraph>
            <Paragraph>Phone Number: +91 953 801 1662</Paragraph>
          </Section>

          <Section>
            <SectionTitle>Get in Touch</SectionTitle>
            {alert && <AlertMessage severity={alert.type}>{alert.message}</AlertMessage>}
            <Form onSubmit={handleSubmit}>
              <InputWrapper>
                <InputField
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <InputLabel htmlFor="name">Your Name</InputLabel>
              </InputWrapper>
              <InputWrapper>
                <InputField
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <InputLabel htmlFor="email">Email</InputLabel>
              </InputWrapper>
              <InputWrapper>
                <InputField
                  type="tel"
                  name="phone"
                  id="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
                <InputLabel htmlFor="phone">Phone Number</InputLabel>
              </InputWrapper>
              <InputWrapper>
                <InputField
                  type="text"
                  name="subject"
                  id="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
                <InputLabel htmlFor="subject">Subject</InputLabel>
              </InputWrapper>
              <TextAreaWrapper>
                <TextArea
                  name="message"
                  id="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  required
                />
                <TextAreaLabel htmlFor="message">Message</TextAreaLabel>
              </TextAreaWrapper>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleChange}
                />
                I agree to the Terms and Conditions
              </CheckboxLabel>
              <SubmitButton type="submit">Submit</SubmitButton>
            </Form>
          </Section>
        </div>
      </ContentContainer>

      <Footer>
        <div>© 2025 DealSphere. All rights reserved.</div>
        <div>
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
          <a href="/contact">Contact</a>
        </div>
      </Footer>
    </Container>
  );
}

export default ContactPage;